import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wrapper } from '@googlemaps/react-wrapper';
import {
    Box,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    useBoolean
} from '@chakra-ui/react';

import { DataContext } from '../../../core/contexts';
import { LandClass, RoleClass } from '../../../core/land';
import { PageCard, PageCardFooter } from '../shared/pageCard';

import { LandMapModal } from './';

export default function LandMapCard(props: {
    data: LandClass;
    role: RoleClass;
}): React.ReactElement {
    const mapRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();
    const { updateLand } = useContext(DataContext);

    const [land, setLand] = useState<LandClass>(props.data);
    const [mapData, setMapData] = useState<Record<string, any>>({});
    const [loading, setIsLoading] = useBoolean(true);

    const loadMap = (data: any) => {
        if (data?.lat && data?.lng && mapRef.current) {
            new window.google.maps.Map(mapRef.current!, {
                mapTypeId: google.maps.MapTypeId.HYBRID,
                streetViewControl: false,
                zoom: 15,
                center: {
                    lat: data.lat,
                    lng: data.lng
                }
            });
        }
    };

    useEffect(() => {
        const m = props.data.map || {};
        switch (m.type) {
            case 'custom':
                setMapData(m);
                break;
            case 'carto':
                setMapData({
                    ...m,
                    carto: m.data.replace(/\s/g, '').split(',')
                });
                break;
            case 'simple':
                setMapData(m);
                setTimeout(() => loadMap(m), 250);
                break;
            default:
                setMapData({
                    type: 'custom',
                    data: props.data.getDefaultMap()
                });
        }
        setIsLoading.off();
        setLand(props.data);
    }, [props.data]);

    return !loading ? (
        <PageCard id='map'>
            <Box
                mx={{ base: -3, md: -7 }}
                mt={{ base: -2, md: -5 }}
                position='relative'
                _before={{
                    content: `'${t('land.loadingMap')}'`,
                    position: 'absolute',
                    zIndex: 1,
                    top: '240px',
                    left: 'calc(50% - 65px)'
                }}
            >
                {mapData.type === 'custom' && (
                    <iframe
                        id='customMap'
                        title='customMap'
                        src={mapData.data}
                        width='100%'
                        height='480'
                        style={{
                            zIndex: 2,
                            position: 'relative'
                        }}
                    />
                )}

                {mapData.type === 'carto' &&
                    (mapData.carto!.length > 1 ? (
                        <Tabs isFitted>
                            <TabList>
                                {mapData.carto!.map((r: any) => (
                                    <Tab key={r}>{r}</Tab>
                                ))}
                            </TabList>

                            <TabPanels>
                                {mapData.carto!.map((r: any) => (
                                    <TabPanel key={r}>
                                        <iframe
                                            id='cartoMap'
                                            title={r}
                                            src={`https://www1.sedecatastro.gob.es/Cartografia/mapa.aspx?refcat=${r}`}
                                            width='100%'
                                            height='480'
                                            style={{
                                                zIndex: 2,
                                                position: 'relative'
                                            }}
                                        />
                                    </TabPanel>
                                ))}
                            </TabPanels>
                        </Tabs>
                    ) : (
                        <iframe
                            id='cartoMap'
                            title={mapData.carto![0]}
                            src={`https://www1.sedecatastro.gob.es/Cartografia/mapa.aspx?refcat=${
                                mapData.carto![0]
                            }`}
                            width='100%'
                            height='480'
                            style={{
                                zIndex: 2,
                                position: 'relative'
                            }}
                        />
                    ))}

                {mapData.type === 'simple' && (
                    <Wrapper
                        libraries={['places']}
                        apiKey={'AIzaSyApdCmbsGzUhZrdQtPy1TY8mGwwkHE4zzc'}
                    >
                        <div
                            id='simpleMap'
                            ref={mapRef}
                            style={{
                                zIndex: 2,
                                position: 'relative',
                                width: '100%',
                                height: '480px'
                            }}
                        />
                    </Wrapper>
                )}
            </Box>
            {!props.role?.isReader() ? (
                <PageCardFooter>
                    <LandMapModal
                        data={land}
                        beforeClose={() => updateLand((land as LandClass).id)}
                    />
                </PageCardFooter>
            ) : (
                <Box my='0.5'></Box>
            )}
        </PageCard>
    ) : (
        <></>
    );
}
